import * as React from "react";
import { styled } from "@mui/material/styles";
import { appHeaderTitle } from "../../common/services/Utilities";
import { Box, Button, Grid, MobileStepper, Typography } from "@mui/material";
import Breadcrumbs from "../../containers/Breadcrumbs";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import { AppBody } from "../AppBody";
import BackButton from "../../containers/BackButton";
import { Link } from "react-router-dom";
import { AppUrl } from "../../config";
import { ReactComponent as RedArrow } from "../../common/assets/images/gfx_RedArrow.svg";
import { ReactComponent as BecomesArrow } from "../../common/assets/images/gfx_ArrowBecomes.svg";
import LeavingFDICDialog from "../LeavingFDICDialog";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";

const PREFIX = "API101";

const classes = {
  body: `${PREFIX}-body`,
  table: `${PREFIX}-table`,
  button: `${PREFIX}-button`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.body}`]: {
    color: theme.palette.primary.main,
    fontWeight: 550,
    fontSize: 16,
    lineHeight: "24px",
    fontStyle: "normal",
  },

  [`& .${classes.table}`]: {
    "border": "1px solid black",
    "borderCollapse": "collapse",
    "& td": {
      border: "1px solid",
      padding: 10,
      fontWeight: "bold",
      color: theme.palette.primary.main,
      textAlign: "center",
    },
    "& th": {
      border: "1px solid",
      fontWeight: "bold",
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary.main,
      padding: 10,
    },
  },

  [`& .${classes.button}`]: {
    "backgroundColor": theme.palette.secondary.main,
    "borderRadius": "2px",
    "boxSizing": "border-box",
    "color": theme.palette.primary.main,
    "border": "none",
    "fontWeight": "bold",
    "width": "80px",
    "height": "32px",

    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    "&:disabled": {
      backgroundColor: "rgba(230, 230, 230, 1)",
    },
  },
}));

const defaultBoxStyles = {
  fontWeight: "bold",
  fontSize: 16,
  display: "inline-block",
};

const defaultBodyStyles = {
  fontWeight: 550,
  fontSize: 16,
  lineHeight: "24px",
  fontStyle: "normal",
};

const tableData = [
  { id: 1, code: "%20", char: "[space]" },
  { id: 2, code: "%21", char: "!" },
  { id: 3, code: "%22", char: '"' },
  { id: 4, code: "%28", char: "(" },
  { id: 5, code: "%29", char: ")" },
  { id: 6, code: "%2C", char: "," },
  { id: 7, code: "%3A", char: ":" },
];

export default function API101() {
  useInitialPageLoad("api101");

  const [activeStep, setActiveStep] = React.useState(0);
  const steps = [
    {
      boxText: (
        <Typography color="#CC0000" sx={defaultBoxStyles}>
          https://api.fdic.gov/banks/
        </Typography>
      ),
      stepLabel: "Step 1 - The Core URL",
      stepDescription:
        "An API query is basically a URL that is sent to our data servers requesting specific information to be returned. To begin, open a new web browser window and enter the URL shown above. This is the core address for all of our API queries.",
    },
    {
      boxText: (
        <>
          <Typography
            sx={{
              color: "#999999",
              ...defaultBoxStyles,
            }}
          >
            https://api.fdic.gov/banks/
          </Typography>
          <Typography color="#CC0000" sx={defaultBoxStyles}>
            financials
          </Typography>
        </>
      ),
      stepLabel: "Step 2 - Designate The Endpoint",
      stepDescription: (
        <>
          <Typography
            color="primary.main"
            sx={defaultBodyStyles}
            style={{ paddingBottom: "15px" }}
          >
            Next, add the “endpoint” to the base URL to target the data you're
            interested in. Endpoints are specialized buckets of data, which we
            have crafted to house data with a certain “point of view”. For more
            about the endpoints we offer and their uses, see our{" "}
            <a href={AppUrl("API")}>API Documentation</a> page for a complete
            list including:
          </Typography>
          <Typography color="primary.main" sx={defaultBodyStyles}>
            <ul>
              <li>
                <em>
                  <b>institutions</b>
                </em>{" "}
                - Insured Institution Data
              </li>
              <li>
                <em>
                  <b>financials</b>
                </em>{" "}
                - Financial Data
              </li>
              <li>
                <em>
                  <b>sod</b>
                </em>{" "}
                - Summary of deposits survey
              </li>
              <li>and more...</li>
            </ul>
          </Typography>
        </>
      ),
    },
    {
      boxText: (
        <>
          <Typography
            sx={{
              color: "#999999",
              ...defaultBoxStyles,
            }}
          >
            https://api.fdic.gov/banks/financials
          </Typography>
          <Typography
            color="#CC0000"
            sx={defaultBoxStyles}
            style={{ display: "inline-flex" }}
          >
            ?
            <span style={{ paddingLeft: "10px" }}>
              <RedArrow />
            </span>
          </Typography>
          <Typography
            color="primary.main"
            sx={defaultBoxStyles}
            style={{ paddingLeft: "10px" }}
          >
            Mandatory for Proper Syntax
          </Typography>
        </>
      ),
      stepLabel: "Step 3 - The Break Before The Query",

      stepDescription: (
        <>
          <Typography color="primary.main" sx={defaultBodyStyles}>
            With the first half of the query completed, it is now properly
            targeted to talk to our API. The second half of your query is where
            you define the data you wish to return. This is called the
            parameters section and it always begins with a "
            <span style={{ color: "#CC0000", fontWeight: "bold" }}>?</span>".
          </Typography>
        </>
      ),
    },
    {
      boxText: (
        <>
          <Typography
            sx={{
              color: "#999999",
              ...defaultBoxStyles,
            }}
          >
            https://api.fdic.gov/banks/financials?
          </Typography>
          <Typography color="primary.main" sx={defaultBoxStyles}>
            <span style={{ color: "#CC0000" }}>api_key=[YourKeyHere]</span>
          </Typography>
        </>
      ),
      stepLabel: "Step 4 - Obtaining And Using An API Key",
      stepDescription: (
        <>
          <Typography
            color="primary.main"
            sx={{ ...defaultBodyStyles, marginBottom: "1rem" }}
          >
            Many public APIs use keys as a way to secure and monitor API usage,
            and BankFind Suite follows this best practice. Users are required to
            obtain a unique “key” to use with every query submitted to our API.
            The key will be a long alphanumeric (upper and lower case) string,
            designed to be unique and just for you. You are responsible for the
            safe keeping of your key. It is recommended that you save the
            original email that contains your API key, as it also contains the
            email address and account ID associated to that key. Lost API keys
            are not retrievable. If you lose your API key, you will have
            re-register to obtain a new one.
          </Typography>

          <LeavingFDICDialog
            webAddress="https://api.data.gov/signup/"
            label="Obtain your API key here"
            bold
          />
          <Typography
            color="primary.main"
            sx={{ ...defaultBodyStyles, marginTop: "1rem" }}
          >
            Once you have received your API key, you are ready to use it. You
            will need to include it as part of your query. Please note the code
            sample above for the proper syntax. Simply replace the content
            “[YourKeyHere]” with your actual key.
          </Typography>
        </>
      ),
    },
    {
      boxText: (
        <>
          <Typography
            sx={{
              color: "#999999",
              ...defaultBoxStyles,
            }}
          >
            https://api.fdic.gov/banks/financials?api_key=[YourKeyHere]
          </Typography>
          <span style={{ color: "#CC0000" }}>&</span>
          <Typography color="primary.main" sx={defaultBoxStyles}>
            filters=VAR:PARAM<span style={{ color: "#CC0000" }}>&</span>
            fields=VAR1, VAR2<span style={{ color: "#CC0000" }}>&</span>
            sort_by:VAR:PARAM
          </Typography>
        </>
      ),
      stepLabel: "Step 5 - Building The Query Using Parameters",
      stepDescription: (
        <>
          <Typography color="primary.main" sx={defaultBodyStyles}>
            The parameters section can be broken up into various subsections
            that shape the data to be returned: what it focuses on, how it
            appears, sorts, and downloads. Your API key, which we’ve already
            covered, is technically the query’s first parameter followed by
            other groups of subsections that are chained together by ending each
            section with an ampersand "
            <span style={{ color: "#CC0000", fontWeight: "bold" }}>&</span>".
            The exception to this rule being the very last subsection, which
            does not have an ampersand.
          </Typography>
          <Typography
            color="primary.main"
            sx={defaultBodyStyles}
            style={{ paddingTop: "15px", paddingBottom: "15px" }}
          >
            Shown above is a generalization, to help you get the idea. We’ll go
            into greater detail of a few of the subsections later on, but for
            now the key take-away is that you have control over the data your
            get back and how it appears by using a chain of subsections to
            sculpt your data.
          </Typography>

          <Typography color="primary.main" sx={defaultBodyStyles}>
            For more information about possible parameter topics, specific
            usage, and syntax, see each endpoints’ section on the
            <a href={AppUrl("API")}> API Documentation</a> page.
          </Typography>
        </>
      ),
    },
    {
      boxText: (
        <Typography color="primary.main" sx={defaultBoxStyles}>
          https://api.fdic.gov/banks/financials
          <span style={{ color: "#CC0000" }}>?</span>
          api_key=[YourKeyHere] <span style={{ color: "#CC0000" }}>&</span>
          filters=ACTIVE:1 AND !(BKCLASS:NC) AND REPDTE:20220930
          <span style={{ color: "#CC0000" }}>&</span>
          fields=ASSET,DEP,CERT,RSSDHCR,NAMEFULL,CITY,STALP,ZIP,REPDTE
          ,BKCLASS,NAMEHCR,OFFDOM,SPECGRP,SUBCHAPS,ESTYMD,INSDATE,EFFDATE
          ,MUTUAL,PARCERT,TRUST,REGAGNT,INSAGNT1,FDICDBS,FDICSUPV,FLDOFF
          ,FED,OCCDIST,OFFOA,CB,INTINC,ILNDOM,ILNFOR,ILS
          ,ICHBAL,ISC,ITRADE,IFREPO,IOTHII
          <span style={{ color: "#CC0000" }}>&</span>sort_by=REPDTE
          <span style={{ color: "#CC0000" }}>&</span> sort_order=DESC
          <span style={{ color: "#CC0000" }}>&</span>limit=25
          <span style={{ color: "#CC0000" }}>&</span>offset=0
          <span style={{ color: "#CC0000" }}>&</span>format=csv
          <span style={{ color: "#CC0000" }}>&</span>download=true
          <span style={{ color: "#CC0000" }}>&</span>filename=data_file
        </Typography>
      ),
      stepLabel: "Step 6 - A Real World API Call Example to Explore",
      stepDescription: (
        <>
          <Typography
            color="primary.main"
            sx={defaultBodyStyles}
            marginBottom="2rem"
          >
            With the basics out of the way, let’s see it in action. Shown above
            is a real world API query which we will look at piece by piece as a
            way to better understand it. As stated previously, the parameters
            section begins after the “
            <span style={{ color: "#CC0000", fontWeight: "bold" }}>?</span>”
            with each subsection ending with “
            <span style={{ color: "#CC0000", fontWeight: "bold" }}>&</span>”,
            except for the very last one. Shown below is our example broken up
            into its constituent pieces to aide this demonstration.
          </Typography>
          <Grid container spacing={2} sx={{ marginTop: 2 }}>
            <Grid size={8} sx={{ alignContent: "center" }}>
              <Typography
                color="primary.main"
                sx={{
                  ...defaultBoxStyles,
                  marginLeft: 6,
                  marginRight: "1rem",
                }}
              >
                https://api.fdic.gov/banks/financials
                <span style={{ color: "#CC0000" }}>?</span>
              </Typography>
            </Grid>
            <Grid
              sx={{
                borderLeft: "2px solid #CC0000",
              }}
              style={{ paddingTop: 0, display: "flex", alignItems: "center" }}
              size={4}
            >
              <Typography color="#CC0000" sx={defaultBoxStyles} paddingLeft={2}>
                Base URL and endpoint ending with “?”
              </Typography>
            </Grid>
            <Grid size={8} sx={{ alignContent: "center" }}>
              <Typography
                color="primary.main"
                sx={{ ...defaultBoxStyles, marginLeft: 6, marginRight: "1rem" }}
              >
                <span style={{ color: "#CC0000" }}>api_key=</span>[YourKeyHere]
                <span style={{ color: "#CC0000" }}>&</span>
              </Typography>
            </Grid>
            <Grid
              sx={{
                borderLeft: "2px solid #CC0000",
                alignItems: "center",
                display: "flex",
              }}
              size={4}
            >
              <Typography color="#CC0000" sx={defaultBoxStyles} paddingLeft={2}>
                Your API Key
              </Typography>
            </Grid>
            <Grid size={8}>
              <Typography
                color="primary.main"
                sx={{
                  ...defaultBoxStyles,
                  marginLeft: 6,
                  overflowWrap: "anywhere",
                  marginRight: "1rem",
                }}
              >
                <span style={{ color: "#CC0000" }}>filters=</span>ACTIVE:1
                AND!(BKCLASS:NC) AND REPDTE:20220930{" "}
                <span style={{ color: "#CC0000" }}>&</span>{" "}
                <span style={{ color: "#CC0000" }}>fields=</span>
                ASSET,DEP,CERT,RSSDHCR,NAMEFULL,CITY,STALP,ZIP,REPDTE,BKCLASS,NAMEHCR,OFFDOM,SPECGRP,SUBCHAPS,ESTYMD,INSDATE,EFFDATE,MUTUAL,PARCERT,TRUST,REGAGNT,INSAGNT1,FDICDBS,FDICSUPV,FLDOFF,FED,OCCDIST,OFFOA,CB,INTINC,ILNDOM,ILNFOR,ILS,ICHBAL,ISC,ITRADE,IFREPO,IOTHII
                <span style={{ color: "#CC0000" }}>&</span>
              </Typography>
            </Grid>
            <Grid
              sx={{
                borderLeft: "2px solid #CC0000",
              }}
              style={{ paddingTop: 0, display: "flex", alignItems: "center" }}
              size={4}
            >
              <Typography color="#CC0000" sx={defaultBoxStyles} paddingLeft={2}>
                Data definition parameters:
                <br />
                Filters and Fields (data variables)
              </Typography>
            </Grid>
            <Grid size={8}>
              <Typography
                color="#CC0000"
                sx={{ ...defaultBoxStyles, marginLeft: 6, marginRight: "1rem" }}
              >
                sort_by=<span style={{ color: "#1A4480" }}>REPDTE</span>&
                <br />
                sort_order=<span style={{ color: "#1A4480" }}>DESC</span>&
                <br />
                limit=<span style={{ color: "#1A4480" }}>25</span>&
                <br />
                offset=<span style={{ color: "#1A4480" }}>0</span>&
                <br />
                format=<span style={{ color: "#1A4480" }}>csv</span>&
                <br />
                download=<span style={{ color: "#1A4480" }}>true</span>&
                <br />
                filename=<span style={{ color: "#1A4480" }}>data_file</span>
              </Typography>
            </Grid>
            <Grid
              sx={{
                borderLeft: "2px solid #CC0000",
              }}
              style={{ paddingTop: 0, display: "flex", alignItems: "center" }}
              size={4}
            >
              <Typography color="#CC0000" sx={defaultBoxStyles} paddingLeft={2}>
                Display options parameters: Specific to the API controlling:
                sorting options, return limits, download options, and more. The
                very last parameter subsection does not end with “&”.
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      boxText: (
        <Typography sx={{ color: "#999999", ...defaultBoxStyles }}>
          https://api.fdic.gov/banks/financials?api_key=[YourKeyHere]&
          <span style={{ color: "#CC0000" }}>filters=</span>
          <span style={{ color: "#1A4480" }}>
            ACTIVE:1 AND !(BKCLASS:NC) AND REPDTE:20220930
          </span>
          <span style={{ color: "#CC0000" }}>&</span>
          fields=ASSET,DEP,CERT,RSSDHCR,NAMEFULL,CITY,STALP,ZIP,REPDTE
          ,BKCLASS,NAMEHCR,OFFDOM,SPECGRP,SUBCHAPS,ESTYMD,INSDATE,EFFDATE
          ,MUTUAL,PARCERT,TRUST,REGAGNT,INSAGNT1,FDICDBS,FDICSUPV,FLDOFF
          ,FED,OCCDIST,OFFOA,CB,INTINC,ILNDOM,ILNFOR,ILS
          ,ICHBAL,ISC,ITRADE,IFREPO
          ,IOTHII&sort_by=REPDTE&sort_order=DESC&limit=25&offset=0&format=csv&download=true&filename=data_file
        </Typography>
      ),
      stepLabel: "Step 7 - Filters and Boolean Statements",
      stepDescription: (
        <>
          <Typography
            color="primary.main"
            sx={defaultBodyStyles}
            style={{ paddingBottom: "15px" }}
          >
            The “filters” subsection is a way to focus the scope of your query
            by targeting/limiting it in a controlled manner. Filters can become
            very precise when augmented with boolean logic. Using the format of
            <span style={{ color: "#CC0000", fontWeight: "bold" }}>
              {" "}
              VAR:VALUE
            </span>
            , you will specify the variable(s) and value(s) you wish to filter
            with. A simple example is to filter the returned data to only have
            records that match a specific institution, in which case you’d
            filter for the CERT variable (VAR) and a specific Cert Numer you
            wanted (VALUE).
          </Typography>
          <Typography
            color="primary.main"
            sx={defaultBodyStyles}
            style={{ paddingBottom: "15px" }}
          >
            Our example shown above combines multiple
            <span style={{ color: "#CC0000", fontWeight: "bold" }}>
              {" "}
              VAR:VALUE{" "}
            </span>
            parameters connected together with boolean terms. The
            <span style={{ color: "#CC0000", fontWeight: "bold" }}>
              {" "}
              AND{" "}
            </span>{" "}
            is easy to see. We also see the syntax for a boolean NOT statement,
            which is created with the syntax{" "}
            <span style={{ color: "#CC0000", fontWeight: "bold" }}>
              !(VAR:VALUE)
            </span>
            . The NOT parameter will exclude all records that match the stated
            parameter from the results. This filter statement parsed into
            english would read “All records with active institutions (ACTIVE:1)
            AND NOT non-insured non-deposit commercial banks !(BKCLASS:NC) AND a
            report date of Sept 30, 2022 (REPDTE:20220930).
          </Typography>
          <Typography
            color="primary.main"
            sx={defaultBodyStyles}
            style={{ paddingBottom: "15px" }}
          >
            The example shown is not the only way to filter your data. Notably,
            you can introduce ranges to finely target the data you want. The{" "}
            <a href={AppUrl("API")}>API Documentation</a> page can help you form
            the syntax for all of the filter options and boolean functions for
            each of the endpoints. Also see the{" "}
            <Link
              id="glossary-link"
              to={"/help?helpTopic=glossary-and-variable-definitions"}
            >
              Variables and Glossary Definitions
            </Link>{" "}
            page to assist you with possible values to use when filtering
            variables. You can also use the YAML files (listed on the API
            Documentation page) for each endpoint for even more information on
            the possible values to use when filtering.
          </Typography>
          <Typography variant="h4" style={{ padding: 0 }}>
            Important Note On The Use of Spaces
          </Typography>
          <Typography color="primary.main" sx={defaultBodyStyles}>
            With only one exception, the best practice is to never use spaces in
            your API query. Spaces can create bad syntax which will can break
            your query, giving no results or errors. The only exception to this
            rule is the “filters” subsection shown. Spaces <b>are required</b>{" "}
            in the filters subsection to properly parse boolean statements. When
            troubleshooting a query that doesn’t give you the results you
            expect, check your syntax and use of spaces.
          </Typography>
        </>
      ),
    },
    {
      boxText: (
        <Typography sx={{ color: "#999999", ...defaultBoxStyles }}>
          https://api.fdic.gov/banks/financials?api_key=[YourKeyHere]&filters=ACTIVE:1
          AND !(BKCLASS:NC) AND REPDTE:20220930&{" "}
          <span style={{ color: "#CC0000" }}>fields=</span>
          <span style={{ color: "#1A4480" }}>
            ASSET,DEP,CERT,RSSDHCR,NAMEFULL,CITY,STALP,ZIP,REPDTE
            ,BKCLASS,NAMEHCR,OFFDOM,SPECGRP,SUBCHAPS,ESTYMD,INSDATE
            ,EFFDATE,MUTUAL,PARCERT,TRUST,REGAGNT,INSAGNT1,FDICDBS
            ,FDICSUPV,FLDOFF,FED,OCCDIST,OFFOA,CB,INTINC,ILNDOM
            ,ILNFOR,ILS,ICHBAL,ISC,ITRADE,IFREPO,IOTHII
          </span>
          <span style={{ color: "#CC0000" }}>&</span>sort_by=REPDTE&
          sort_order=DESC&limit=25&offset=0&format=csv&download=true&filename=data_file
        </Typography>
      ),
      stepLabel: "Step 8 - The Data You’re Looking For",
      stepDescription: (
        <>
          <Typography
            color="primary.main"
            sx={defaultBodyStyles}
            style={{ paddingBottom: "15px" }}
          >
            The “fields” subsection is the heart of your data. Here you can
            provide a list of the variables you want. The syntax is simple: all
            variables must be <b>UPPERCASE</b>, and each variable is separated
            by a <b>comma</b>.
          </Typography>
          <Typography
            color="primary.main"
            sx={defaultBodyStyles}
            style={{ paddingBottom: "15px" }}
          >
            Determining which variables to query can be tricky as there are a
            lot to choose from, especially when using the financials endpoint.
            For that endpoint, the API provides a default list of about 150 of
            the most commonly used variables if no fields have been specified.
            Use the the{" "}
            <Link
              id="glossary-link"
              to={"/help?helpTopic=glossary-and-variable-definitions"}
            >
              Glossary and Variables Definitions
            </Link>{" "}
            interface to research your own customized list. You can also use the
            custom report interface in{" "}
            <Link id="financial-report-link" to={AppUrl("FinancialReporting")}>
              Financial Reporting
            </Link>{" "}
            to curate your list.
          </Typography>
          <Typography color="primary.main" sx={defaultBodyStyles}>
            <b>Note:</b> Variables in the fields subsection should only be
            separated by commas. Do not add additional spaces to aide visual
            separation. While it may be possible to do, it will effect how your
            query reads after execution by the browser, which may make you wish
            that you hadn’t (see slide 11.)
          </Typography>
        </>
      ),
    },
    {
      boxText: (
        <Typography sx={{ color: "#999999", ...defaultBoxStyles }}>
          https://api.fdic.gov/banks/financials?api_key=[YourKeyHere]&filters=ACTIVE:1
          AND !(BKCLASS:NC) AND REPDTE:20220930& fields=ASSET,DEP,CERT,RSSDHCR
          ,NAMEFULL,CITY,STALP,ZIP,REPDTE,BKCLASS,NAMEHCR,OFFDOM,SPECGRP
          ,SUBCHAPS,ESTYMD,INSDATE,EFFDATE,MUTUAL,PARCERT,TRUST,REGAGNT
          ,INSAGNT1,FDICDBS,FDICSUPV,FLDOFF,FED,OCCDIST,OFFOA
          ,CB,INTINC,ILNDOM,ILNFOR,ILS,ICHBAL,ISC,ITRADE,IFREPO,IOTHII&
          <span style={{ color: "#CC0000" }}>sort_by=</span>
          <span style={{ color: "#1A4480" }}>REPDTE</span>
          <span style={{ color: "#CC0000" }}>& sort_order=</span>
          <span style={{ color: "#1A4480" }}>DESC</span>
          <span style={{ color: "#CC0000" }}>&limit=25&offset=</span>
          <span style={{ color: "#1A4480" }}>0</span>
          <span style={{ color: "#CC0000" }}>&format=</span>
          <span style={{ color: "#1A4480" }}>csv</span>
          <span style={{ color: "#CC0000" }}>&download=</span>
          <span style={{ color: "#1A4480" }}>true</span>
          <span style={{ color: "#CC0000" }}>&filename=</span>
          <span style={{ color: "#1A4480" }}>data_file</span>
        </Typography>
      ),
      stepLabel: "Step 9 - Various Housekeeping Parameters",
      stepDescription: (
        <>
          <Typography
            color="primary.main"
            sx={defaultBodyStyles}
            style={{ paddingBottom: "15px" }}
          >
            The remaining subsections are available for you to define how the
            data will appear when returned or downloaded. You can choose to have
            your results sorted by a certain variable, shown here sorting by
            REPDTE or report date. You can also control the sort order, number
            of records returned (in case you are trying to access a very large
            amount of data or have a limited bandwidth connectivity),
            pagination. The API also has built in limitations for the amount of
            records and fields returned, so if you’re having issues with your
            query try using limiters and to chop the data into smaller groups
            for easier transmission.
          </Typography>
          <Typography color="primary.main" sx={defaultBodyStyles}>
            For additional help with the possible choices and syntax to use for
            these housekeeping parameters, please see the{" "}
            <a href={AppUrl("API")}>API Documentation</a> page.
          </Typography>
        </>
      ),
    },
    {
      boxText: (
        <Typography color="primary.main" sx={defaultBoxStyles}>
          https://api.fdic.gov/banks/financials?api_key=[YourKeyHere]&filters=ACTIVE:1
          AND !(BKCLASS:NC) AND REPDTE:20220930& fields=ASSET,DEP,CERT,RSSDHCR
          ,NAMEFULL,CITY,STALP,ZIP,REPDTE,BKCLASS,NAMEHCR,OFFDOM,SPECGRP
          ,SUBCHAPS,ESTYMD,INSDATE,EFFDATE,MUTUAL,PARCERT,TRUST,REGAGNT
          ,INSAGNT1,FDICDBS,FDICSUPV,FLDOFF,FED,OCCDIST,OFFOA,CB
          ,INTINC,ILNDOM,ILNFOR,ILS,ICHBAL,ISC,ITRADE,IFREPO
          ,IOTHII&sort_by=REPDTE&
          sort_order=DESC&limit=25&offset=0&format=csv&download=true&filename=data_file
        </Typography>
      ),
      stepLabel: "Step 10 - How To Use the Query",
      stepDescription: (
        <>
          <Typography
            color="primary.main"
            sx={defaultBodyStyles}
            style={{ paddingBottom: "15px" }}
          >
            You may have noticed that this API query looks like a website URL,
            and you would be right. Instead of asking your browser to retrieve
            and display a specific webpage, you are asking your browser to
            connect to our API server and return the data you’ve requested in
            your the query. This means that like other URLs, you can bookmark
            your API queries to access them again and again. You can also write
            and save them with a text editor for easy access, or share them with
            colleagues so that they can view the exact data you are.
          </Typography>
          <Typography color="primary.main" sx={defaultBodyStyles}>
            To help you get up and running using the API, we offer a spreadsheet
            of our most commonly used financial reports, encoded into API
            queries.{" "}
            <a href={`${AppUrl("API")}/All Financial Reports.xlsx`}>
              Download it here now
            </a>
            , or you can find it on the{" "}
            <a href={AppUrl("API")}>API Documentation</a> page.
          </Typography>
        </>
      ),
    },
    {
      customText: (
        <Grid container>
          <Grid size={5}>
            <Typography
              variant="h4"
              style={{ display: "flex", justifyContent: "center" }}
            >
              API Query Excerpt As Typed
            </Typography>
            <Typography color="primary.main" sx={defaultBoxStyles}>
              filters=ACTIVE:1 AND !(BKCLASS:NC) AND
              REPDTE:20220930&fields=ASSET, DEP, CERT, RSSDHCR,
            </Typography>
          </Grid>
          <Grid size={2}>
            <BecomesArrow />
          </Grid>
          <Grid sx={{ paddingLeft: "40px" }} size={5}>
            <Typography
              variant="h4"
              style={{ display: "flex", justifyContent: "center" }}
            >
              API Query Excerpt As Browser Modified It
            </Typography>
            <Typography
              color="primary.main"
              sx={defaultBoxStyles}
              style={{ overflowWrap: "anywhere" }}
            >
              filters=ACTIVE<span style={{ color: "#CC0000" }}>%3A</span>1
              <span style={{ color: "#CC0000" }}>%20</span>AND
              <span style={{ color: "#CC0000" }}>%20%21%28</span>
              BKCLASS<span style={{ color: "#CC0000" }}>%3A</span>NC
              <span style={{ color: "#CC0000" }}>%29%20</span>AND
              <span style={{ color: "#CC0000" }}>%20</span>
              REPDTE<span style={{ color: "#CC0000" }}>%3A</span>
              20220930&fields=ASSET,
              <span style={{ color: "#CC0000" }}>%20</span>
              DEP,<span style={{ color: "#CC0000" }}>%20</span>CERT,
              <span style={{ color: "#CC0000" }}>%20</span>RSSDHCR,
            </Typography>
          </Grid>
        </Grid>
      ),
      stepLabel: "Step 11 - Character Re-Encoding",
      stepDescription: (
        <Grid container>
          <Grid size={8}>
            <Typography
              color="primary.main"
              sx={defaultBodyStyles}
              style={{ paddingBottom: "15px" }}
            >
              As you become more familiar with API queries, you will begin to
              notice that what appears in your browser’s URL bar may not exactly
              match what you typed/pasted in. Browsers do not like certain
              common characters in URLs, and will re-encode those characters
              from their original input, into a new format that makes sense to
              the browser, but not to us.
            </Typography>
            <Typography
              color="primary.main"
              sx={defaultBodyStyles}
              style={{ paddingBottom: "15px" }}
            >
              In addition, our API Documentation page (powered by a third party
              tool called “Swagger”) also performs character-to-code
              substitutions with the “Try It Out” functionality, making the
              generated example code harder to understand. It’s just another
              reason for you to brush up on your own query writing skills.
            </Typography>
            <Typography
              color="primary.main"
              sx={defaultBodyStyles}
              style={{ paddingBottom: "15px" }}
            >
              There isn’t much that can be done about these substitutions, other
              than being aware that they happen. This is another reason why the
              minimal use of spaces is a best practice. In this instance it’s
              for legibility.
            </Typography>
            <Typography color="primary.main" sx={defaultBodyStyles}>
              <b>Helpful Hints</b>
              <ul>
                <li>
                  Craft your queries in a text editor and save the originals for
                  easier edits and modifications.
                </li>
                <li>
                  If you notice re-encoded characters on a query you were sent
                  or tried to save, use this chart to help you transpose the
                  changed characters back into something recognizable and easier
                  to read. Use a text editor to perform a global search &
                  replace for the re-encoded entries, changing them back to
                  their legible equivalents.
                </li>
              </ul>
            </Typography>
            <Typography color="primary.main" sx={defaultBodyStyles}>
              These are the most common characters you’ll see, but not a
              complete set. If you need assistance with other re-encoded UTF-8
              characters, please do a web search for a complete look up table
              with your favorite search engine.
            </Typography>
          </Grid>
          <Grid sx={{ paddingLeft: "40px" }} size={4}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Displayed Code</th>
                  <th>UTF-8 Character</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row) => (
                  <tr key={row.id}>
                    <td>{row.code}</td>
                    <td>{row.char}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Grid>
        </Grid>
      ),
    },
  ];
  const maxSteps = steps.length;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const introParagraph = (
    <>
      {appHeaderTitle("api101")}
      <Typography variant="body2" id="api-101-link" className={classes.body}>
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=FinRegData"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
      <Typography
        variant="body2"
        id="api-101-description"
        className={classes.body}
      >
        APIs have become powerful and indispensable tools for making large data
        sources accessible to the public. However, if you you’re not a developer
        or coder they may seem alien and intimidating. The purpose of this page
        is to demystify this new concept and help you get started.
        <br />
        <br />
        If you are not familiar with using an API, you probably do not know
        accessing one is as simple as using a browser and typing in a URL. What
        you include in that URL is a wide array of parameters to craft and
        sculpt your query to suite your needs, which can include:
      </Typography>
      <Typography
        id="api-101-description-list"
        className={classes.body}
        component="div"
      >
        <ul>
          <li>The base address to point to the API (mandatory)</li>
          <li>Which API end point is being queried (mandatory)</li>
          <li>Obtaining and using an API Key</li>
          <li>Variables you are interested in (data)</li>
          <li>
            Filtering on key parameters with boolean phrasing (conditional
            arguments like AND, OR and even exclude results with NOT)
          </li>
          <li>Filtering with ranges</li>
          <li>
            Display and behavioral options (sorting, number of results, download
            results etc)
          </li>
        </ul>
      </Typography>
    </>
  );

  return (
    <Root>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Breadcrumbs currentPageBreadCrumbTitle="API 101" />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        introText={introParagraph}
        backButton={
          <BackButton
            app="BulkData"
            buttonLabel="Back to Bulk Data Home"
            variant="dark"
          />
        }
      >
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <Typography
              variant="h2"
              style={{ paddingBottom: 20, paddingTop: 20 }}
            >
              The Fundamentals of Building an API Query
            </Typography>
            <Typography variant="body2" className={classes.body}>
              Use the interface below to learn about the basics of forming a
              BankFind Suite API query by seeing the process step by step. We
              will also provide tips and insights to help you get started and to
              keep you coming back.
            </Typography>
            <MobileStepper
              variant="text"
              steps={maxSteps}
              sx={{
                width: 250,
                margin: "auto",
                paddingBottom: 2,
                fontSize: ".875rem",
                fontWeight: "bold",
                color: "#1A4480",
              }}
              position="static"
              activeStep={activeStep}
              nextButton={
                <Button
                  id="nextStep"
                  className={classes.button}
                  size="small"
                  onClick={handleNext}
                  disabled={activeStep === maxSteps - 1}
                >
                  Next
                </Button>
              }
              backButton={
                <Button
                  id="prevStep"
                  className={classes.button}
                  size="small"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                >
                  Previous
                </Button>
              }
            />
            <Grid container>
              <Grid size={1}></Grid>
              <Grid size={10}>
                {steps[activeStep].boxText ? (
                  <Box
                    style={{
                      padding: 20,
                      overflowWrap: "anywhere",
                      borderRadius: "8px",
                      opacity: 1,
                    }}
                    sx={{
                      bgcolor: "#f5f9fc",
                      border: 1,
                      marginTop: -1,
                      borderColor: "#797979",
                    }}
                  >
                    {steps[activeStep].boxText}
                  </Box>
                ) : (
                  steps[activeStep].customText
                )}

                <Typography
                  id="stepLabel"
                  variant="h4"
                  style={{
                    paddingTop: "20px",
                    paddingBottom: "20px",
                  }}
                >
                  {steps[activeStep].stepLabel}
                </Typography>
                <Typography variant="body2" className={classes.body}>
                  {steps[activeStep].stepDescription}
                </Typography>
              </Grid>
              <Grid size={1}></Grid>
            </Grid>
          </Grid>
        </Grid>
      </AppBody>
    </Root>
  );
}
